import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDevices, useBranches, useCreateDevice, useUpdateDevice } from '@/api';
import { useAuth } from '@/context/AuthContext';
import { Card, CardContent, Button, Input, Badge, Modal } from '@/components/ui';
import { BranchFilterSelect, LoadingSpinner, Pagination } from '@/components/common';
import { Plus, Wifi, WifiOff, Settings, Server } from 'lucide-react';
import { extractPaginatedData } from '@/utils/pagination';
import { useDebouncedValue } from '@/utils/useDebouncedValue';

export default function DevicesPage() {
  const perPage = 9;
  const navigate = useNavigate();
  const { can } = useAuth();
  const canManage = can('manage_devices');
  const [page, setPage] = useState(1);
  const [showForm, setShowForm] = useState(false);
  const [editingDevice, setEditingDevice] = useState<any>(null);
  const [branchFilter, setBranchFilter] = useState('');
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebouncedValue(search.trim());
  const { data: devicesData, isLoading } = useDevices({
    page,
    per_page: perPage,
    branch_id: branchFilter,
    search: debouncedSearch,
  });
  const { data: branchesData } = useBranches();
  const createMutation = useCreateDevice();
  const updateMutation = useUpdateDevice();

  const { items: devices, meta } = extractPaginatedData<any>(devicesData, page, perPage);
  const branches = Array.isArray(branchesData?.data)
    ? branchesData.data
    : branchesData?.data?.data || [];

  if (isLoading) {
    return <LoadingSpinner />;
  }

  const getStatusBadge = (status: string) => {
    const statusMap: Record<string, { variant: string; label: string }> = {
      active: { variant: 'success', label: 'Подключен' },
      inactive: { variant: 'secondary', label: 'Неактивен' },
      maintenance: { variant: 'warning', label: 'Обслуживание' },
      disconnected: { variant: 'destructive', label: 'Нет связи' },
    };
    const statusInfo = statusMap[status] || { variant: 'secondary', label: status };
    return <Badge variant={statusInfo.variant as any}>{statusInfo.label}</Badge>;
  };

  return (
    <div className="space-y-6 page-shell">
      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div>
          <h1 className="text-3xl font-bold">Терминалы Hikvision</h1>
          <p className="text-sm text-gray-500 mt-1">
            Управляйте терминалами DS-K1T341AMF по IP, логину и синхронизации через ISAPI.
          </p>
        </div>
        {canManage && (
          <Button onClick={() => setShowForm(true)}>
            <Plus className="w-4 h-4 mr-2" />
            Добавить терминал
          </Button>
        )}
      </div>

      <div className="grid grid-cols-1 gap-4 xl:grid-cols-3">
        <Input
          placeholder="Поиск по SN, названию или IP"
          value={search}
          onChange={(e) => {
            setPage(1);
            setSearch(e.target.value);
          }}
        />
        <BranchFilterSelect
          alwaysShow
          className="w-full px-4 py-2 border rounded-lg"
          value={branchFilter}
          onChange={(value) => {
            setPage(1);
            setBranchFilter(value);
          }}
        />
        <div className="rounded-lg border px-4 py-2 text-sm text-gray-600 bg-gray-50">
          Адрес ISAPI: <span className="font-mono">/ISAPI/AccessControl/AcsEvent?format=json</span>
        </div>
      </div>

      <Modal
        isOpen={showForm || !!editingDevice}
        onClose={() => {
          setShowForm(false);
          setEditingDevice(null);
        }}
        title={editingDevice ? 'Редактировать терминал Hikvision' : 'Новый терминал Hikvision'}
      >
        <DeviceForm
          initialData={editingDevice}
          branches={branches}
          onSubmit={async (data) => {
            if (editingDevice) {
              await updateMutation.mutateAsync({ id: editingDevice.id, data });
            } else {
              await createMutation.mutateAsync(data);
            }
            setShowForm(false);
            setEditingDevice(null);
          }}
          onCancel={() => {
            setShowForm(false);
            setEditingDevice(null);
          }}
        />
      </Modal>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
        {devices.map((device: any) => (
          <Card key={device.id} className="cursor-pointer hover:shadow-lg transition">
            <CardContent className="p-6">
              <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                <div className="flex items-center gap-3">
                  {device.status === 'active' ? (
                    <Wifi className="w-8 h-8 text-green-600" />
                  ) : (
                    <WifiOff className="w-8 h-8 text-red-600" />
                  )}
                  <div>
                    <h3 className="font-semibold">{device.device_name}</h3>
                    <p className="text-sm text-gray-500">{device.model || 'DS-K1T341AMF'}</p>
                  </div>
                </div>
                {getStatusBadge(device.status)}
              </div>

              <div className="space-y-2 text-sm">
                <div className="flex justify-between gap-3">
                  <span className="text-gray-500">SN:</span>
                  <span className="font-mono text-right">{device.device_sn}</span>
                </div>
                <div className="flex justify-between gap-3">
                  <span className="text-gray-500">Филиал:</span>
                  <span className="text-right">{device.branch?.name || '-'}</span>
                </div>
                <div className="flex justify-between gap-3">
                  <span className="text-gray-500">ISAPI:</span>
                  <span className="font-mono text-right">
                    {device.protocol || 'http'}://{device.ip_address}:{device.port || 80}
                  </span>
                </div>
                <div className="flex justify-between gap-3">
                  <span className="text-gray-500">Последняя синхронизация:</span>
                  <span className="text-right">
                    {device.last_sync_at
                      ? new Date(device.last_sync_at).toLocaleString('ru-RU')
                      : 'Еще не было'}
                  </span>
                </div>
              </div>

              <div className="mt-4 flex flex-col gap-2 sm:flex-row">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => navigate(`/devices/${device.id}`)}
                >
                  <Settings className="w-4 h-4 mr-2" />
                  Управление
                </Button>
                {canManage && (
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => setEditingDevice(device)}
                  >
                    <Server className="w-4 h-4" />
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
      <Pagination meta={meta} onPageChange={setPage} />

      {devices.length === 0 && (
        <div className="text-center py-12">
          <WifiOff className="w-16 h-16 mx-auto text-gray-400 mb-4" />
          <h3 className="text-lg font-medium text-gray-900">Терминалы Hikvision не найдены</h3>
          <p className="text-gray-500">Добавьте устройство DS-K1T341AMF и запустите синхронизацию.</p>
        </div>
      )}
    </div>
  );
}

function DeviceForm({
  initialData,
  branches,
  onSubmit,
  onCancel,
}: {
  initialData?: any;
  branches: any[];
  onSubmit: (data: any) => void;
  onCancel: () => void;
}) {
  const [formData, setFormData] = useState({
    device_name: initialData?.device_name || '',
    device_sn: initialData?.device_sn || '',
    branch_id: initialData?.branch_id?.toString() || '',
    vendor: 'hikvision',
    model: initialData?.model || 'DS-K1T341AMF',
    device_type: initialData?.device_type || 'MULTI',
    ip_address: initialData?.ip_address || '',
    protocol: initialData?.protocol || 'http',
    port: String(initialData?.port || 80),
    device_username: initialData?.device_username || 'admin',
    device_password: '',
    auth_type: initialData?.auth_type || 'digest',
    location_description: initialData?.location_description || '',
    is_active: initialData?.is_active ?? true,
  });

  const handleSubmit = () => {
    const payload: any = {
      ...formData,
      branch_id: Number(formData.branch_id),
      port: Number(formData.port || 80),
    };

    if (initialData && !formData.device_password.trim()) {
      delete payload.device_password;
    }

    onSubmit(payload);
  };

  return (
    <div className="space-y-4">
      <select
        className="w-full px-4 py-2 border rounded-lg"
        value={formData.branch_id}
        onChange={(e) => setFormData({ ...formData, branch_id: e.target.value })}
      >
        <option value="">Выберите филиал</option>
        {branches.map((branch: any) => (
          <option key={branch.id} value={branch.id}>
            {branch.name}
          </option>
        ))}
      </select>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <Input
          placeholder="Название терминала"
          value={formData.device_name}
          onChange={(e) => setFormData({ ...formData, device_name: e.target.value })}
        />
        <Input
          placeholder="Серийный номер (SN)"
          value={formData.device_sn}
          onChange={(e) => setFormData({ ...formData, device_sn: e.target.value })}
        />
        <Input
          placeholder="Модель"
          value={formData.model}
          onChange={(e) => setFormData({ ...formData, model: e.target.value })}
        />
        <select
          className="w-full px-4 py-2 border rounded-lg"
          value={formData.device_type}
          onChange={(e) => setFormData({ ...formData, device_type: e.target.value })}
        >
          <option value="MULTI">MULTI</option>
          <option value="FACE">FACE</option>
          <option value="FINGERPRINT">FINGERPRINT</option>
          <option value="RFID">RFID</option>
        </select>
        <Input
          placeholder="IP-адрес"
          value={formData.ip_address}
          onChange={(e) => setFormData({ ...formData, ip_address: e.target.value })}
        />
        <Input
          placeholder="Порт"
          value={formData.port}
          onChange={(e) => setFormData({ ...formData, port: e.target.value })}
        />
        <select
          className="w-full px-4 py-2 border rounded-lg"
          value={formData.protocol}
          onChange={(e) => setFormData({ ...formData, protocol: e.target.value })}
        >
          <option value="http">http</option>
          <option value="https">https</option>
        </select>
        <select
          className="w-full px-4 py-2 border rounded-lg"
          value={formData.auth_type}
          onChange={(e) => setFormData({ ...formData, auth_type: e.target.value })}
        >
          <option value="digest">Digest-аутентификация</option>
          <option value="basic">Basic-аутентификация</option>
        </select>
        <Input
          placeholder="Логин"
          value={formData.device_username}
          onChange={(e) => setFormData({ ...formData, device_username: e.target.value })}
        />
        <Input
          placeholder={initialData ? 'Пароль (введите для изменения)' : 'Пароль'}
          type="password"
          value={formData.device_password}
          onChange={(e) => setFormData({ ...formData, device_password: e.target.value })}
        />
      </div>

      <Input
        placeholder="Описание местоположения"
        value={formData.location_description}
        onChange={(e) => setFormData({ ...formData, location_description: e.target.value })}
      />

      <div className="rounded-lg bg-gray-50 border px-4 py-3 text-sm">
        <div className="font-medium mb-1">Адрес ISAPI</div>
        <div className="font-mono break-all">
          {formData.protocol}://{formData.ip_address || 'DEVICE_IP'}:{formData.port || '80'}
          /ISAPI/AccessControl/AcsEvent?format=json
        </div>
      </div>

      <label className="flex items-center gap-2 cursor-pointer">
        <input
          type="checkbox"
          checked={formData.is_active}
          onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
          className="w-4 h-4 rounded border-gray-300"
        />
        <span className="text-sm text-gray-700">Терминал активен</span>
      </label>

      <div className="flex flex-col gap-2 sm:flex-row">
        <Button onClick={handleSubmit} className="flex-1">
          Сохранить
        </Button>
        <Button onClick={onCancel} variant="outline" className="flex-1">
          Отмена
        </Button>
      </div>
    </div>
  );
}


